import { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Plug,
  MonitorUp,
  UserCheck,
  Trash2,
  AlertTriangle,
  Monitor,
  Cpu,
  MemoryStick,
  Globe,
  HardDrive,
  Users,
  Package,
  Network,
  Pencil,
  Check,
  X,
  SlidersHorizontal,
  Server,
  Eye,
  EyeOff,
  Gauge,
  Tv,
  Thermometer,
  Flame,
  Activity,
  Wrench,
  Power,
  Clock,
  FolderOpen,
  TerminalSquare,
  ListChecks,
} from 'lucide-react';
import { api } from '../lib/api.jsx';
import { useDashboard } from '../views/dashboardContext.jsx';
import { SparklineChart } from '../components/SparklineChart.jsx';
import { SystemUpdatesPanel } from '../components/SystemUpdatesPanel.jsx';
import { ContainersPanel } from '../components/ContainersPanel.jsx';
import { DeviceActivityPanel } from '../components/DeviceActivityPanel.jsx';
import { HostedSitesPanel } from '../components/HostedSitesPanel.jsx';
import { UptimeHeatmap } from '../components/UptimeHeatmap.jsx';
import { PerformanceHistory } from '../components/PerformanceHistory.jsx';
import { PhysicalDisksPanel, mediaBadgeOf } from '../components/PhysicalDisksPanel.jsx';
import { MemoryBreakdown } from '../components/MemoryBreakdown.jsx';
import { AlertThresholdsModal } from '../components/AlertThresholdsModal.jsx';
import { CveLinks } from '../components/CveLinks.jsx';
import { RemoteSessionModal } from '../components/RemoteSessionModal.jsx';
import { RestartModal, scheduledLabel } from '../components/RestartModal.jsx';
import { MaintenanceModal } from '../components/MaintenanceModal.jsx';
import { UpdateChannelControl } from '../components/UpdateChannelControl.jsx';
import { RouterDetail } from './RouterDetail.jsx';
import { hasPermission } from '../lib/permissions.jsx';
import { useRealtime } from '../lib/realtime.jsx';
import { fmtMachineType, fmtOnlineStatus, isServerMachine } from '../lib/format.jsx';
import { ALERT_SEV_COLORS, ALERT_SEV_LABELS, ALERT_CAT_LABELS } from '../components/AlertsTable.jsx';
import { buildAlertTaskText, buildCveTaskText, ComitBonChip, ComitTaskButton } from '../lib/comit.jsx';

const sectionCls = 'bg-card border border-rule rounded-2xl px-5 py-4 mb-4';
const sTitleCls = 'text-sm font-bold text-fg-2 mb-3.5 flex items-center gap-2';
const tableCls = 'w-full border-collapse text-xs';
const thCls = 'px-3 py-2 text-left text-muted-2 font-medium text-[11px] uppercase border-b border-rule';
const tdCls = 'px-3 py-2 text-fg-2 border-b border-sidebar';
const inputCls = 'bg-sidebar border border-rule rounded-lg px-3.5 py-2 text-fg-2 text-xs outline-none';

const fmtMB = (mb) => (mb >= 1024 ? (mb / 1024).toFixed(1) + ' Go' : mb + ' Mo');
const fmtDate = (d) => (d ? new Date(d).toLocaleString('fr-FR') : '—');

const KV = ({ label, value }) => (
  <div className="text-xs">
    <div className="text-muted-2 text-[11px] mb-0.5">{label}</div>
    <div className="text-fg font-mono text-[12.5px]">{value ?? '—'}</div>
  </div>
);

// Champ sensible (clé produit Windows…) — masqué par défaut avec toggle
const SecretKV = ({ label, value }) => {
  const [shown, setShown] = useState(false);
  const masked = value ? value.replace(/[A-Z0-9]/g, '•') : '';
  if (!value) return <KV label={label} value={null} />;
  return (
    <div className="text-xs">
      <div className="text-muted-2 text-[11px] mb-0.5 flex items-center gap-1.5">
        <span>{label}</span>
        <button
          type="button"
          onClick={() => setShown((s) => !s)}
          className="text-muted-2 hover:text-fg-2 cursor-pointer p-0 m-0 bg-transparent border-none inline-flex items-center"
          title={shown ? 'Masquer' : 'Afficher'}
        >
          {shown ? <EyeOff size={12} strokeWidth={1.75} /> : <Eye size={12} strokeWidth={1.75} />}
        </button>
      </div>
      <div className="text-fg font-mono text-[12.5px]">{shown ? value : masked}</div>
    </div>
  );
};

const ProgressBar = ({ p, w }) => (
  <div className="bg-rule rounded h-2.5 overflow-hidden mt-1" style={{ width: w || '100%' }}>
    <div
      className="h-full rounded transition-[width] duration-500"
      style={{
        width: `${Math.min(p, 100)}%`,
        background: p >= 95 ? '#ef4444' : p >= 80 ? '#f59e0b' : '#22c55e',
      }}
    />
  </div>
);

const SEV_BADGE_COLORS = {
  critical: 'bg-crit/15 border-crit/30 text-red-300',
  high: 'bg-warn/15 border-warn/30 text-amber-300',
  medium: 'bg-blue-500/15 border-blue-500/30 text-blue-400',
  low: 'bg-slate-500/15 border-slate-500/30 text-muted',
  secure: 'bg-ok/12 border-ok/30 text-emerald-300',
  unknown: 'bg-slate-500/10 border-slate-500/20 text-muted-2',
};

const SevBadge = ({ level, text, count }) => (
  <span
    className={`px-2.5 py-0.5 rounded-md text-[10px] font-bold tracking-wide uppercase border ${
      SEV_BADGE_COLORS[level] || SEV_BADGE_COLORS.unknown
    }`}
  >
    {text || level}
    {count !== undefined ? ` (${count})` : ''}
  </span>
);

const NIC_TYPE_CLS = {
  'Wi-Fi': 'bg-violet-500/20 text-violet-400',
  Ethernet: 'bg-blue-500/20 text-blue-400',
  Virtual: 'bg-slate-500/20 text-muted',
};

export function DeviceDetail({ agentId, onBack, user, perms }) {
  const { navigate } = useDashboard();
  const canManageConfig = hasPermission(perms, 'ManageConfig');
  // Le lien vers la fiche client n'est proposé qu'à qui peut l'ouvrir : sans
  // ViewClients, la cible répondrait 403 et le lien serait un cul-de-sac.
  const canViewClients = hasPermission(perms, 'ViewClients');
  const canInstallUpdates = hasPermission(perms, 'InstallUpdates');
  const canManageAlerts = hasPermission(perms, 'ManageAlerts');
  const canRunCommands = hasPermission(perms, 'RunCommands');
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nicOnlyUp, setNicOnlyUp] = useState(() => localStorage.getItem('mh_nic_filter') !== 'all');
  const [swSearch, setSwSearch] = useState('');
  const [swFilter, setSwFilter] = useState('all');
  const [swSort, setSwSort] = useState('name');
  const [swPage, setSwPage] = useState(0);
  const SW_PAGE_SIZE = 25;
  const [uninstallConfirm, setUninstallConfirm] = useState(false);
  const [uninstalling, setUninstalling] = useState(false);
  const [editingLabel, setEditingLabel] = useState(false);
  const [labelDraft, setLabelDraft] = useState('');
  const [savingLabel, setSavingLabel] = useState(false);
  const [showThresholds, setShowThresholds] = useState(false);
  const [showMaintenance, setShowMaintenance] = useState(false);
  const [maintenance, setMaintenance] = useState(null);
  const [showRestart, setShowRestart] = useState(false);
  // Prise en main (R6) : cibles et capacités lues à part de la fiche, le temps
  // d'un inventaire (≤ 5 min), donc rafraîchies à la même cadence que la maintenance.
  const canRemote = hasPermission(perms, 'RemoteConnect');
  const canTools = hasPermission(perms, 'RunCommands');
  const canTargets = canRemote || canTools;
  const [remoteTargets, setRemoteTargets] = useState(null);
  const [showRemoteModal, setShowRemoteModal] = useState(false);
  const [remoteBusy, setRemoteBusy] = useState(false);
  const [scheduledCmds, setScheduledCmds] = useState([]);

  useEffect(() => {
    const load = async () => {
      const r = await api.get('/devices/' + agentId);
      setD(r);
      setLoading(false);
    };
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, [agentId]);

  const loadRemoteTargets = () =>
    api.get('/platform/remote/agents/' + agentId + '/targets').then((r) => setRemoteTargets(r)).catch(() => {});
  useEffect(() => {
    if (!canTargets) return undefined;
    loadRemoteTargets();
    const iv = setInterval(loadRemoteTargets, 30000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, canTargets]);
  // Session ouverte ou terminée (ici ou dans la fenêtre de la visionneuse) : sans
  // ce rafraîchissement, `activeSessionId` restait jusqu'à 30 s celui d'une session
  // finie, et le bouton y renvoyait (« terminée par le technicien »).
  useRealtime('RemoteSupportChanged', (e) => {
    if (canTargets && String(e?.agentId ?? e?.AgentId) === String(agentId)) loadRemoteTargets();
  });

  const startRemote = async (choice) => {
    setRemoteBusy(true);
    // Fenêtre ouverte AVANT l'await : après, le navigateur ne la rattache plus au clic
    // et la bloque comme une pop-up.
    const w = window.open('about:blank', 'fx-remote-' + agentId, 'popup,width=1366,height=860');
    try {
      const r = await api.fetch('/platform/remote/sessions', {
        method: 'POST',
        body: JSON.stringify({ agentId: Number(agentId), ...choice }),
      });
      if (!r.ok) {
        let msg = 'Impossible de démarrer le contrôle à distance (' + r.status + ').';
        try { const j = await r.json(); if (j?.error) msg = j.error; } catch { /* non JSON */ }
        w?.close();
        alert(msg);
        return;
      }
      const s = await r.json();
      setShowRemoteModal(false);
      if (w) w.location.href = '/#/remote/' + s.id;
      else window.open('/#/remote/' + s.id, 'fx-remote-' + agentId, 'popup,width=1366,height=860');
    } catch (e) {
      w?.close();
      alert('Erreur : ' + (e.message || e));
    } finally {
      setRemoteBusy(false);
    }
  };

  // Outils sans écran (fichiers, terminal, tâches) : session « outils » sur la
  // console ; si une session vit déjà sur le poste, le serveur la rejoint.
  const startTool = async (outil) => {
    setRemoteBusy(true);
    const nom = 'fx-tools-' + agentId;
    const features = 'popup,width=1280,height=820';
    const w = window.open('about:blank', nom, features);
    try {
      const r = await api.fetch('/platform/remote/sessions', {
        method: 'POST',
        body: JSON.stringify({ agentId: Number(agentId), target: 'console', mode: 'outils' }),
      });
      if (!r.ok) {
        let msg = 'Impossible d’ouvrir l’outil (' + r.status + ').';
        try { const j = await r.json(); if (j?.error) msg = j.error; } catch { /* non JSON */ }
        w?.close();
        alert(msg);
        return;
      }
      const s = await r.json();
      const url = '/#/remote/' + s.id + '?outil=' + outil;
      if (w) w.location.href = url;
      else window.open(url, nom, features);
    } catch (e) {
      w?.close();
      alert('Erreur : ' + (e.message || e));
    } finally {
      setRemoteBusy(false);
    }
  };

  const onRemoteClick = () => {
    const t = remoteTargets;
    if (!t) return;
    if (t.activeSessionId) {
      // Rejoindre passe par la création : la plateforme rejoint la session si elle
      // vit encore, et en ouvre une nouvelle si elle vient de se terminer — jamais
      // de renvoi vers une session finie sur la foi d'un état périmé.
      if (window.confirm('Une session de contrôle à distance est déjà active sur ce poste. La rejoindre ?'))
        startRemote({ target: 'console' });
      return;
    }
    const sessions = t.sessions || [];
    if (t.isServer || sessions.length > 1 || sessions.some((s) => s.sessionType === 'RDP')) setShowRemoteModal(true);
    else startRemote({ target: 'console' });
  };

  const loadMaintenance = () => {
    api.get('/platform/maintenance-windows?agentId=' + agentId).then((r) => setMaintenance(r));
  };

  // Commandes planifiées (redémarrage, mises à jour) : bannière visible sans
  // ouvrir la modale. Même cadence que la maintenance, la liste change rarement.
  const loadScheduled = () => {
    api.get('/platform/devices/' + agentId + '/scheduled-commands').then((r) => setScheduledCmds(r || []));
  };

  useEffect(() => {
    loadScheduled();
    const iv = setInterval(loadScheduled, 30000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  useEffect(() => {
    loadMaintenance();
    const iv = setInterval(loadMaintenance, 30000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  const saveLabel = async () => {
    setSavingLabel(true);
    const r = await api.patch('/devices/' + agentId, { label: labelDraft });
    setSavingLabel(false);
    if (r) {
      setD((prev) => (prev ? { ...prev, agent: { ...prev.agent, label: r.label } } : prev));
      setEditingLabel(false);
    } else {
      alert('Échec de la mise à jour du libellé.');
    }
  };

  if (loading) return <div className="text-muted-2 p-10">Chargement...</div>;
  if (!d) return <div className="text-crit p-10">Appareil introuvable</div>;

  const a = d.agent;
  // Un routeur MikroTik est une ligne `agents` (platform = routeros) sans
  // inventaire de poste : sa fiche est un composant à part. Le branchement est
  // placé APRÈS tous les hooks de ce composant (comme les deux retours
  // précédents), donc l'ordre des hooks reste identique d'un rendu à l'autre ;
  // les données déjà chargées ici lui sont passées pour ne pas doubler les sondages.
  if (a.platform === 'routeros') {
    return (
      <RouterDetail
        agentId={agentId}
        d={d}
        perms={perms}
        reload={async () => setD(await api.get('/devices/' + agentId))}
        maintenance={maintenance}
        loadMaintenance={loadMaintenance}
        scheduledCmds={scheduledCmds}
        loadScheduled={loadScheduled}
      />
    );
  }
  const inv = d.inventory;
  const hbs = d.heartbeats || [];
  // La plateforme vient de l'agent (renseignée à l'enregistrement) : c'est elle
  // qui décide de ce qui a du sens à afficher. Les postes enregistrés avant la
  // 1.9 n'ont pas le champ — l'absence vaut « windows », comme côté serveur.
  const isLinux = (a.platform || inv?.platform) === 'linux';

  // Volume ▸ type de support, construit depuis les disques physiques : c'est eux
  // qui portent la liste des volumes, pour ne pas dupliquer le type sur chaque
  // système de fichiers.
  const mediaByVolume = {};
  for (const disk of inv?.physicalDisks || []) {
    const badge = mediaBadgeOf(disk.mediaType);
    if (!badge) continue;
    for (const volume of disk.volumes || []) mediaByVolume[volume] = badge;
  }

  const uptime = inv?.lastBootTime
    ? (() => {
        const ms = Date.now() - new Date(inv.lastBootTime).getTime();
        const days = Math.floor(ms / 86400000);
        const hrs = Math.floor((ms % 86400000) / 3600000);
        const mins = Math.floor((ms % 3600000) / 60000);
        return `${days}j ${hrs}h ${mins}m`;
      })()
    : '—';

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <button
          onClick={onBack}
          className="bg-rule border-none rounded-lg px-3.5 py-2 text-muted cursor-pointer text-xs font-semibold hover:bg-rule-2 inline-flex items-center gap-1.5"
        >
          <ArrowLeft size={14} strokeWidth={1.75} /> Retour
        </button>
        <div className="flex-1">
          {editingLabel ? (
            <div className="flex gap-1.5 items-center">
              <input
                autoFocus
                value={labelDraft}
                placeholder={a.computerName}
                onChange={(e) => setLabelDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveLabel();
                  else if (e.key === 'Escape') setEditingLabel(false);
                }}
                className="text-xl font-bold text-fg bg-sidebar border border-blue-500 rounded-md px-2.5 py-1 outline-none min-w-[280px]"
              />
              <button
                onClick={saveLabel}
                disabled={savingLabel}
                className="bg-blue-500 border-none rounded-md px-3 py-1.5 text-white cursor-pointer text-xs font-semibold disabled:opacity-60 inline-flex items-center"
              >
                {savingLabel ? '...' : <Check size={14} strokeWidth={2.5} />}
              </button>
              <button
                onClick={() => setEditingLabel(false)}
                className="bg-transparent border border-rule rounded-md px-2.5 py-1.5 text-muted cursor-pointer text-xs inline-flex items-center"
              >
                <X size={14} strokeWidth={2} />
              </button>
            </div>
          ) : (
            <h1
              className="text-[22px] font-bold text-fg cursor-pointer inline-flex items-center gap-2"
              title="Cliquer pour modifier le libellé"
              onClick={() => {
                setLabelDraft(a.label || '');
                setEditingLabel(true);
              }}
            >
              {a.label || a.computerName}
              <Pencil size={12} strokeWidth={1.75} className="text-muted-2 opacity-60" />
            </h1>
          )}
          <div className="text-xs text-muted-2 mt-0.5 flex items-center gap-2">
            <span
              className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${
                isLinux ? 'bg-amber-500/15 text-amber-300' : 'bg-blue-500/15 text-blue-400'
              }`}
              title={inv?.osName || undefined}
            >
              {isLinux ? 'Linux' : 'Windows'}
            </span>
            {/* Le client d'abord : à l'ouverture d'une fiche, « à qui est cette
                machine » précède tout le reste. Il vient de l'API avec l'agent,
                donc aucune requête supplémentaire.

                Les morceaux sont assemblés puis séparés, et non semés de « · »
                conditionnels : un client supprimé ou un site orphelin laissait
                sinon un séparateur en tête de ligne. */}
            {[
              a.client?.name ? (
                canViewClients ? (
                  <button
                    key="client"
                    type="button"
                    onClick={() => navigate('clientDetail', { client: a.client.id })}
                    title="Ouvrir la fiche du client"
                    className="bg-transparent border-none p-0 text-blue-400 hover:underline cursor-pointer text-xs font-semibold"
                  >
                    {a.client.name}
                  </button>
                ) : (
                  <span key="client" className="text-fg-2 font-semibold">
                    {a.client.name}
                  </span>
                )
              ) : null,
              a.site?.name ? <span key="site">{a.site.name}</span> : null,
              <span key="uid">
                {a.label ? a.computerName + ' · ' : ''}
                {a.uid || '#' + a.id}
              </span>,
            ]
              .filter(Boolean)
              .map((node, i) => (
                <span key={node.key} className="inline-flex items-center gap-2">
                  {i > 0 && <span className="text-muted-3">·</span>}
                  {node}
                </span>
              ))}
          </div>
        </div>

        {canRemote && remoteTargets?.capabilities?.includes('ecran') && (
          // L'état de l'accès non supervisé est dans l'infobulle et, quand l'usager
          // doit accepter, dans l'icône de fin : une légende sous le bouton le
          // décalait par rapport aux autres boutons de la ligne.
          <button
            onClick={onRemoteClick}
            disabled={!remoteTargets.supported || remoteBusy}
            title={
              !remoteTargets.supported
                ? remoteTargets.reason || 'Indisponible'
                : 'Ouvrir le contrôle à distance de ce poste dans une nouvelle fenêtre — ' +
                  (remoteTargets.unattended === false
                    ? 'accès non supervisé désactivé : l’usager devra accepter'
                    : 'accès non supervisé activé')
            }
            className="bg-blue-500 border-none rounded-lg px-4 py-2 text-white cursor-pointer text-xs font-semibold flex items-center gap-1.5 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <MonitorUp size={14} strokeWidth={1.75} /> Contrôle à distance
            {remoteTargets.supported && remoteTargets.unattended === false && (
              <UserCheck size={14} strokeWidth={1.75} className="opacity-80" aria-label="L’usager devra accepter" />
            )}
          </button>
        )}
        {canTools &&
          [
            ['fichiers', 'Fichiers', FolderOpen],
            ['terminal', 'Terminal', TerminalSquare],
            ['processus', 'Tâches', ListChecks],
          ].map(([cap, label, Icone]) =>
            remoteTargets?.capabilities?.includes(cap) ? (
              <button
                key={cap}
                onClick={() => startTool(cap === 'processus' ? 'taches' : cap)}
                disabled={!remoteTargets.supported || remoteBusy}
                title={!remoteTargets.supported ? remoteTargets.reason || 'Indisponible' : label + ' à distance, sans afficher l’écran'}
                className="bg-card border border-rule-3 rounded-lg px-4 py-2 text-fg-2 cursor-pointer text-xs font-semibold flex items-center gap-1.5 hover:text-fg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Icone size={14} strokeWidth={1.75} /> {label}
              </button>
            ) : null,
          )}
        {a.dwServiceAgentId && (
          <button
            onClick={async () => {
              try {
                const r = await api.post('/platform/dwservice/session/' + agentId + '?mode=desktop');
                if (r.url) window.open(r.url, '_blank', 'noopener');
                else alert('Echec: impossible de creer la session DWService');
              } catch (e) {
                alert('Erreur DWService: ' + (e.message || e));
              }
            }}
            className="bg-violet-500 border-none rounded-lg px-4 py-2 text-white cursor-pointer text-xs font-semibold flex items-center gap-1.5 hover:bg-violet-600"
          >
            <Plug size={14} strokeWidth={1.75} /> DWService
          </button>
        )}

        <div className="flex items-center gap-1.5">
          <div className={`w-2 h-2 rounded-full ${a.isOnline ? 'bg-ok' : 'bg-crit'}`} />
          <span
            className={`text-xs font-semibold ${a.isOnline ? 'text-emerald-300' : 'text-red-300'}`}
            title={
              !a.isOnline && a.lastHeartbeat ? `Dernier heartbeat : ${fmtDate(a.lastHeartbeat)}` : undefined
            }
          >
            {fmtOnlineStatus(a.isOnline, a.lastHeartbeat)}
          </span>
        </div>

        <button
          onClick={() => setShowThresholds(true)}
          className="bg-rule border border-rule-3 rounded-lg px-3.5 py-2 text-muted cursor-pointer text-xs font-semibold hover:bg-rule-2 inline-flex items-center gap-1.5"
          title="Configurer les seuils d'alertes pour ce poste"
        >
          <SlidersHorizontal size={14} strokeWidth={1.75} /> Seuils d'alertes
        </button>

        {canRunCommands && (
          <button
            onClick={() => setShowRestart(true)}
            className="bg-rule border border-rule-3 rounded-lg px-3.5 py-2 text-muted cursor-pointer text-xs font-semibold hover:bg-rule-2 inline-flex items-center gap-1.5"
            title="Redémarrer cet appareil maintenant ou à une heure planifiée"
          >
            <Power size={14} strokeWidth={1.75} /> Redémarrer
          </button>
        )}

        {canManageAlerts && !maintenance?.active && (
          <button
            onClick={() => setShowMaintenance(true)}
            className="bg-rule border border-rule-3 rounded-lg px-3.5 py-2 text-muted cursor-pointer text-xs font-semibold hover:bg-rule-2 inline-flex items-center gap-1.5"
            title="Suspendre temporairement les alertes et notifications sur cet appareil"
          >
            <Wrench size={14} strokeWidth={1.75} /> Maintenance
          </button>
        )}

        {user?.role === 0 && (
          <button
            onClick={() => setUninstallConfirm(true)}
            className="bg-crit/12 border border-crit/35 rounded-lg px-3.5 py-2 text-red-300 cursor-pointer text-xs font-semibold hover:bg-crit/20 inline-flex items-center gap-1.5"
          >
            <Trash2 size={14} strokeWidth={1.75} /> Désinstaller
          </button>
        )}
      </div>

      {maintenance?.active && (
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl px-4 py-2.5 mb-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-[12.5px] text-fg-2 inline-flex items-center gap-2">
            <Wrench size={14} strokeWidth={1.75} className="text-blue-400 flex-shrink-0" />
            <span>
              <span className="font-semibold">En maintenance</span>
              {' jusqu\'à '}
              {maintenance.active.endsAt
                ? new Date(maintenance.active.endsAt).toLocaleString('fr-FR')
                : "arrêt manuel"}
              {' — '}
              {maintenance.active.reason}
            </span>
          </div>
          {canManageAlerts && (
            <button
              onClick={async () => {
                await api.post('/platform/maintenance-windows/' + maintenance.active.id + '/end', {});
                loadMaintenance();
              }}
              className="bg-transparent border border-blue-500/40 rounded-lg px-3 py-1 text-blue-400 cursor-pointer text-[11px] font-semibold hover:bg-blue-500/15 flex-shrink-0"
            >
              Terminer
            </button>
          )}
        </div>
      )}

      {scheduledCmds.length > 0 && (
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl px-4 py-2.5 mb-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-[12.5px] text-fg-2 inline-flex items-center gap-2">
            <Clock size={14} strokeWidth={1.75} className="text-blue-400 flex-shrink-0" />
            <span>
              {scheduledCmds
                .map((c) => `${scheduledLabel(c)} planifié le ${new Date(c.scheduledAt).toLocaleString('fr-FR')}`)
                .join(' · ')}
            </span>
          </div>
          <button
            onClick={() => setShowRestart(true)}
            className="bg-transparent border border-blue-500/40 rounded-lg px-3 py-1 text-blue-400 cursor-pointer text-[11px] font-semibold hover:bg-blue-500/15 flex-shrink-0"
          >
            Gérer
          </button>
        </div>
      )}

      {showRemoteModal && (
        <RemoteSessionModal
          targets={remoteTargets}
          computerName={a.label || a.computerName}
          busy={remoteBusy}
          onChoose={startRemote}
          onClose={() => setShowRemoteModal(false)}
        />
      )}

      {showRestart && (
        <RestartModal
          agentId={agentId}
          label={a.label || a.computerName}
          isServer={isServerMachine(a.machineType)}
          scheduled={scheduledCmds}
          canRun={canRunCommands}
          onClose={(changed) => {
            setShowRestart(false);
            if (changed) {
              loadScheduled();
              loadMaintenance();
            }
          }}
        />
      )}

      {showMaintenance && (
        <MaintenanceModal
          agentId={agentId}
          label={a.label || a.computerName}
          onClose={(changed) => {
            setShowMaintenance(false);
            if (changed) loadMaintenance();
          }}
        />
      )}

      {showThresholds && (
        <AlertThresholdsModal
          agentId={agentId}
          computerName={a.label || a.computerName}
          canEdit={canManageConfig}
          onClose={() => setShowThresholds(false)}
        />
      )}

      {/* Uninstall confirmation modal */}
      {uninstallConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[1000]">
          <div className="bg-card border border-red-950 rounded-2xl p-8 w-[420px] shadow-2xl">
            <div className="text-lg font-bold text-fg mb-2 flex items-center gap-2">
              <AlertTriangle size={18} strokeWidth={1.75} className="text-warn" />
              Confirmer la désinstallation
            </div>
            <div className="text-[13px] text-muted mb-5 leading-relaxed">
              L'agent sera définitivement désinstallé de <strong className="text-fg">{a.computerName}</strong>
              .{' '}
              {isLinux
                ? 'Le service systemd sera arrêté et supprimé, ainsi que /opt/fonxtio-agent et sa configuration.'
                : 'Le service Windows sera arrêté et tous les fichiers supprimés.'}{' '}
              Cette action est irréversible.
            </div>
            <div className="flex gap-2.5 justify-end">
              <button
                onClick={() => setUninstallConfirm(false)}
                className="bg-rule border border-rule-3 rounded-lg px-4 py-2 text-muted cursor-pointer text-[13px] font-semibold"
              >
                Annuler
              </button>
              <button
                disabled={uninstalling}
                onClick={async () => {
                  setUninstalling(true);
                  try {
                    const r = await api.post('/platform/commands/uninstall/' + agentId);
                    setUninstallConfirm(false);
                    alert(
                      r?.message ||
                        "Désinstallation envoyée. L'agent passera hors ligne dans quelques secondes."
                    );
                    onBack();
                  } catch (e) {
                    alert('Erreur : ' + (e.message || e));
                  } finally {
                    setUninstalling(false);
                  }
                }}
                className="bg-crit border-none rounded-lg px-4 py-2 text-white cursor-pointer text-[13px] font-semibold disabled:opacity-60 inline-flex items-center gap-1.5"
              >
                {uninstalling ? (
                  'Envoi...'
                ) : (
                  <>
                    <Trash2 size={14} strokeWidth={1.75} /> Désinstaller définitivement
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Two-column grid */}
      <div className="grid grid-cols-2 gap-4 items-start w-full">
        <div>
          <div className={sectionCls}>
            <div className={sTitleCls}>
              <Monitor size={16} strokeWidth={1.75} /> Système d'exploitation
            </div>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-3">
              <KV label="OS" value={inv?.osName} />
              {isLinux ? (
                <>
                  {/* Sur Linux, « édition » et « build » d'os-release sont l'ID de
                      distribution et le nom de code : les intituler autrement
                      qu'en Windows évite de faire chercher un numéro de build. */}
                  <KV label="Distribution" value={inv?.osEdition} />
                  <KV label="Version" value={inv?.osVersion} />
                  <KV label="Nom de code" value={inv?.osBuild} />
                  <KV label="Noyau" value={inv?.kernelVersion} />
                </>
              ) : (
                <>
                  <KV label="Édition" value={inv?.osEdition} />
                  <KV label="Version" value={inv?.osVersion} />
                  <KV label="Build" value={inv?.osBuild} />
                  <KV label="ID publication" value={inv?.osReleaseId} />
                </>
              )}
              <KV label="Architecture" value={inv?.osArchitecture} />
              <KV label="Uptime" value={uptime} />
              <KV label="Dernier démarrage" value={fmtDate(inv?.lastBootTime)} />
              <KV label="Type" value={fmtMachineType(a.machineType)} />
              <KV
                label={isLinux ? 'Domaine DNS' : 'Domaine'}
                value={
                  inv
                    ? inv.isDomain
                      ? inv.domainOrWorkgroup
                      : inv.domainOrWorkgroup + (isLinux ? '' : ' (Workgroup)')
                    : '—'
                }
              />
              {isLinux && inv?.packageManagerName && (
                <KV label="Gestionnaire de paquets" value={inv.packageManagerName} />
              )}
              <UpdateChannelControl
                agent={a}
                osArchitecture={inv?.osArchitecture}
                canManage={canManageConfig}
                onChanged={(updateChannel) =>
                  setD((prev) => (prev ? { ...prev, agent: { ...prev.agent, updateChannel } } : prev))
                }
              />
            </div>
          </div>

          <div className={sectionCls}>
            <div className={sTitleCls}>
              <Server size={16} strokeWidth={1.75} /> Identification matérielle
            </div>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-3">
              <KV
                label="Fabricant / Modèle"
                value={
                  inv && (inv.systemManufacturer || inv.systemModel)
                    ? [inv.systemManufacturer, inv.systemModel].filter(Boolean).join(' — ')
                    : null
                }
              />
              <KV label="Numéro de série" value={inv?.systemSerialNumber} />
              <KV label="BIOS — fabricant" value={inv?.biosManufacturer} />
              <KV
                label="BIOS — version"
                value={
                  inv && (inv.biosVersion || inv.biosReleaseDate)
                    ? [
                        inv.biosVersion,
                        inv.biosReleaseDate
                          ? new Date(inv.biosReleaseDate).toLocaleDateString('fr-FR')
                          : null,
                      ]
                        .filter(Boolean)
                        .join(', ')
                    : null
                }
              />
              {!isLinux && <SecretKV label="Clé de produit Windows" value={inv?.windowsProductKey} />}
              {/* Même colonne en base pour les deux plateformes : préfixe SID sous
                  Windows, /etc/machine-id sous Linux. Cf. AlertCategory.DuplicateMachineId. */}
              <KV label={isLinux ? 'ID machine' : 'SID Windows'} value={inv?.machineSid} />
            </div>
            {inv?.sharedSidPeers && inv.sharedSidPeers.length > 0 && (
              <div className="mt-3 bg-crit/10 border border-crit/30 rounded-lg px-3.5 py-2.5">
                <div className="flex items-start gap-2">
                  <AlertTriangle size={15} strokeWidth={2} className="text-red-300 shrink-0 mt-0.5" />
                  <div className="flex-1 text-[12px]">
                    <div className="font-semibold text-red-300 mb-1">
                      {isLinux ? 'ID machine dupliqué' : 'SID Windows dupliqué'} —{' '}
                      {inv.sharedSidPeers.length + 1} postes affectés
                    </div>
                    <div className="text-fg-2">
                      {isLinux
                        ? "Clonage probable d'une VM sans régénération de /etc/machine-id. Casse les baux DHCP (DUID identique) et l'identité systemd. Machines partageant cet identifiant :"
                        : "Clonage probable sans sysprep. Casse partage d'imprimante, NLA et certaines GPO locales. Postes partageant ce SID :"}
                    </div>
                    <ul className="mt-1 text-fg-2 list-disc list-inside">
                      {inv.sharedSidPeers.map((p) => (
                        <li key={p.id} className="font-mono text-[11.5px]">
                          {p.label || p.computerName}
                        </li>
                      ))}
                    </ul>
                    <div className="text-muted-2 text-[11px] mt-1.5">
                      Solution : exécuter{' '}
                      <code className="font-mono">
                        {isLinux
                          ? 'rm -f /etc/machine-id && systemd-machine-id-setup'
                          : 'sysprep /generalize'}
                      </code>{' '}
                      {isLinux
                        ? 'sur toutes les machines affectées sauf une, puis redémarrer.'
                        : 'sur tous les postes affectés sauf un.'}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className={sectionCls}>
            <div className={sTitleCls}>
              <Cpu size={16} strokeWidth={1.75} /> Processeur
            </div>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-3">
              <KV label="Modèle" value={inv?.processorName} />
              <KV label="Cœurs" value={inv?.processorCores?.toString()} />
              <KV
                label="Utilisation actuelle"
                value={inv ? inv.processorUsagePercent.toFixed(1) + '%' : '—'}
              />
              {/* La charge moyenne compte les processus prêts à s'exécuter, pas un
                  pourcentage : elle se lit par rapport au nombre de cœurs (1.00 par
                  cœur = pleinement occupé sans file d'attente). */}
              {isLinux && inv && (
                <KV
                  label="Charge (1 / 5 / 15 min)"
                  value={`${(inv.loadAverage1 ?? 0).toFixed(2)} / ${(inv.loadAverage5 ?? 0).toFixed(2)} / ${(
                    inv.loadAverage15 ?? 0
                  ).toFixed(2)}`}
                />
              )}
            </div>
            {isLinux && inv?.processorCores > 0 && (inv.loadAverage1 ?? 0) > inv.processorCores && (
              <div className="mt-2.5 flex items-center gap-1.5 text-[11.5px] text-amber-300">
                <Activity size={13} strokeWidth={2} />
                Charge supérieure au nombre de cœurs ({inv.processorCores}) — des processus attendent le
                processeur.
              </div>
            )}
            {hbs.length >= 2 && (
              <div className="mt-3.5">
                <div className="text-[11px] text-muted-2 mb-1">{`Historique CPU — ${hbs.length} mesures`}</div>
                <SparklineChart
                  points={hbs
                    .slice()
                    .reverse()
                    .map((hb) => hb.cpuPercent)}
                  timestamps={hbs
                    .slice()
                    .reverse()
                    .map((hb) => hb.timestamp)}
                  color="#3b82f6"
                  yLabel="% CPU"
                  unit="%"
                  maxY={100}
                />
              </div>
            )}
          </div>

          <div className={sectionCls}>
            <div className={sTitleCls}>
              <MemoryStick size={16} strokeWidth={1.75} /> Mémoire
            </div>
            {inv ? (
              <div>
                <MemoryBreakdown inv={inv} fmtMB={fmtMB} />
                {hbs.length >= 2 && (
                  <div className="mt-3.5">
                    <div className="text-[11px] text-muted-2 mb-1">{`Historique mémoire — ${hbs.length} mesures`}</div>
                    <SparklineChart
                      points={hbs
                        .slice()
                        .reverse()
                        .map((hb) => hb.memoryPercent)}
                      timestamps={hbs
                        .slice()
                        .reverse()
                        .map((hb) => hb.timestamp)}
                      color="#8b5cf6"
                      yLabel="% Mém"
                      unit="%"
                      maxY={100}
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="text-muted-2 text-xs">Aucun inventaire</div>
            )}
          </div>

          {inv?.gpus && inv.gpus.length > 0 && (
            <div className={sectionCls}>
              <div className={sTitleCls}>
                <Gauge size={16} strokeWidth={1.75} /> Carte graphique
                {inv.gpus.length > 1 && (
                  <span className="ml-1 text-muted-2 font-normal">({inv.gpus.length} GPU)</span>
                )}
              </div>
              <div className="space-y-3">
                {inv.gpus.map((g, idx) => (
                  <div key={idx} className="border border-rule rounded-lg px-3.5 py-3 bg-sidebar">
                    <div className="flex items-baseline justify-between mb-2">
                      <div className="text-fg text-[12.5px] font-semibold">{g.name || 'GPU inconnu'}</div>
                      {g.vendor && <div className="text-[10.5px] uppercase text-muted-2">{g.vendor}</div>}
                    </div>
                    <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-2.5">
                      <KV label="Pilote" value={g.driverVersion || null} />
                      <KV
                        label="Date pilote"
                        value={g.driverDate ? new Date(g.driverDate).toLocaleDateString('fr-FR') : null}
                      />
                      <KV
                        label="Utilisation"
                        value={g.utilizationPercent > 0 ? g.utilizationPercent.toFixed(1) + '%' : '—'}
                      />
                    </div>
                    {g.vRamTotalMB > 0 && (
                      <div className="mt-3">
                        <div className="flex items-baseline justify-between text-[11px] mb-0.5">
                          <span className="text-muted-2">VRAM dédiée</span>
                          <span className="text-fg-2 font-mono">
                            {fmtMB(g.vRamUsedMB)} / {fmtMB(g.vRamTotalMB)}
                            <span className="text-muted-2 ml-1">({g.vRamUsagePercent.toFixed(1)}%)</span>
                          </span>
                        </div>
                        <ProgressBar p={g.vRamUsagePercent || 0} />
                      </div>
                    )}
                    {g.sharedTotalMB > 0 && (
                      <div className="mt-2">
                        <div className="flex items-baseline justify-between text-[11px] mb-0.5">
                          <span className="text-muted-2">Mémoire partagée</span>
                          <span className="text-fg-2 font-mono">
                            {fmtMB(g.sharedUsedMB)} / {fmtMB(g.sharedTotalMB)}
                            <span className="text-muted-2 ml-1">({g.sharedUsagePercent.toFixed(1)}%)</span>
                          </span>
                        </div>
                        <ProgressBar p={g.sharedUsagePercent || 0} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {hbs.length >= 2 && hbs.some((hb) => (hb.gpuPercent || 0) > 0) && (
                <div className="mt-3.5">
                  <div className="text-[11px] text-muted-2 mb-1">{`Historique GPU — ${hbs.length} mesures`}</div>
                  <SparklineChart
                    points={hbs
                      .slice()
                      .reverse()
                      .map((hb) => hb.gpuPercent || 0)}
                    timestamps={hbs
                      .slice()
                      .reverse()
                      .map((hb) => hb.timestamp)}
                    color="#f59e0b"
                    yLabel="% GPU"
                    unit="%"
                    maxY={100}
                  />
                </div>
              )}
              {hbs.length >= 2 && hbs.some((hb) => (hb.vRamTotalMB || 0) > 0) && (
                <div className="mt-3">
                  <div className="text-[11px] text-muted-2 mb-1">{`Historique VRAM dédiée`}</div>
                  <SparklineChart
                    points={hbs
                      .slice()
                      .reverse()
                      .map((hb) =>
                        hb.vRamTotalMB > 0 ? Math.round((hb.vRamUsedMB / hb.vRamTotalMB) * 100) : 0
                      )}
                    timestamps={hbs
                      .slice()
                      .reverse()
                      .map((hb) => hb.timestamp)}
                    color="#22c55e"
                    yLabel="% VRAM"
                    unit="%"
                    maxY={100}
                  />
                </div>
              )}
            </div>
          )}

          {inv?.thermals && inv.thermals.length > 0 && (
            <div className={sectionCls}>
              <div className={sTitleCls}>
                <Thermometer size={16} strokeWidth={1.75} /> Capteurs thermiques
              </div>
              <div className="space-y-2.5">
                {inv.thermals.map((t, idx) => {
                  const tempColor =
                    t.temperatureC >= 90 ? 'text-crit' : t.temperatureC >= 80 ? 'text-warn' : 'text-fg';
                  return (
                    <div
                      key={idx}
                      className="border border-rule rounded-lg px-3.5 py-2.5 bg-sidebar flex items-center justify-between gap-3"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2">
                          <span className="text-[10.5px] uppercase text-muted-2 font-bold">
                            {t.deviceType}
                          </span>
                          <span className="text-fg-2 text-[12.5px] truncate">{t.deviceName}</span>
                        </div>
                        <div className="text-muted-2 text-[10.5px] mt-0.5">
                          Max {t.maxTemperatureC.toFixed(1)}°C
                          {t.hasThrottleFlag ? ' · flag matériel' : ' · heuristique'}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {t.isThrottling && (
                          <span className="inline-flex items-center gap-1 bg-crit/15 border border-crit/30 rounded px-2 py-0.5 text-[10.5px] uppercase font-bold text-red-300">
                            <Flame size={11} strokeWidth={2} /> Throttle
                          </span>
                        )}
                        <span className={`font-mono text-[15px] font-bold ${tempColor}`}>
                          {t.temperatureC.toFixed(1)}°C
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
              {hbs.length >= 2 && hbs.some((hb) => (hb.cpuTempC || 0) > 0) && (
                <div className="mt-3">
                  <div className="text-[11px] text-muted-2 mb-1">Historique T° CPU</div>
                  <SparklineChart
                    points={hbs
                      .slice()
                      .reverse()
                      .map((hb) => hb.cpuTempC || 0)}
                    timestamps={hbs
                      .slice()
                      .reverse()
                      .map((hb) => hb.timestamp)}
                    color="#ef4444"
                    yLabel="°C"
                    unit="°C"
                    maxY={110}
                  />
                </div>
              )}
              {hbs.length >= 2 && hbs.some((hb) => (hb.gpuTempC || 0) > 0) && (
                <div className="mt-3">
                  <div className="text-[11px] text-muted-2 mb-1">Historique T° GPU</div>
                  <SparklineChart
                    points={hbs
                      .slice()
                      .reverse()
                      .map((hb) => hb.gpuTempC || 0)}
                    timestamps={hbs
                      .slice()
                      .reverse()
                      .map((hb) => hb.timestamp)}
                    color="#fb923c"
                    yLabel="°C"
                    unit="°C"
                    maxY={110}
                  />
                </div>
              )}
            </div>
          )}

          {inv?.displays && inv.displays.length > 0 && (
            <div className={sectionCls}>
              <div className={sTitleCls}>
                <Tv size={16} strokeWidth={1.75} /> Affichage
                <span className="ml-1 text-muted-2 font-normal">
                  ({inv.displayCount || inv.displays.length} {inv.displays.length > 1 ? 'écrans' : 'écran'})
                </span>
              </div>
              <div className="space-y-2.5">
                {inv.displays.map((d, idx) => (
                  <div
                    key={idx}
                    className="border border-rule rounded-lg px-3.5 py-2.5 bg-sidebar flex items-center justify-between gap-3"
                  >
                    <div className="flex-1">
                      <div className="flex items-baseline gap-2 mb-0.5">
                        <span className="text-fg text-[12.5px] font-mono">
                          {d.deviceName?.replace('\\\\.\\', '') || `Écran ${idx + 1}`}
                        </span>
                        {d.isPrimary && (
                          <span className="text-[10px] uppercase bg-rule text-accent px-1.5 py-0.5 rounded">
                            Principal
                          </span>
                        )}
                      </div>
                      <div className="text-muted-2 text-[11px]">
                        {d.resolutionWidth} × {d.resolutionHeight}
                        {d.refreshRateHz > 0 && ` · ${d.refreshRateHz} Hz`}
                        {d.scalingPercent &&
                          d.scalingPercent !== 100 &&
                          ` · Mise à l'échelle ${d.scalingPercent}%`}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className={sectionCls}>
            <div className={sTitleCls}>
              <Globe size={16} strokeWidth={1.75} /> Réseau
            </div>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-3">
              <KV label="Adresse IP" value={inv?.ipAddress || a.ipAddress} />
              <KV label="Adresse MAC" value={inv?.macAddress || a.macAddress} />
              <KV label="Client" value={a.client?.name} />
              <KV label="Site" value={a.site?.name || a.siteId} />
            </div>
          </div>
        </div>

        <div>
          <div className={sectionCls}>
            <div className={sTitleCls}>
              <HardDrive size={16} strokeWidth={1.75} /> {isLinux ? 'Systèmes de fichiers' : 'Disques'}
            </div>
            {inv?.disks?.length > 0 ? (
              inv.disks.map((dk, i) => (
                <div key={i} className={i < inv.disks.length - 1 ? 'mb-3.5' : ''}>
                  <div className="flex justify-between mb-1">
                    <span className="text-xs text-fg-2 font-semibold font-mono flex items-center gap-1.5">
                      {dk.driveLetter} {dk.label ? `(${dk.label})` : ''}
                      {/* Type du support portant ce volume — le lien vient des
                          disques physiques, pas d'un champ dupliqué sur le volume. */}
                      {mediaByVolume[dk.driveLetter] && (
                        <span
                          className={`px-1.5 py-0.5 rounded text-[9.5px] font-bold ${mediaByVolume[dk.driveLetter].cls}`}
                        >
                          {mediaByVolume[dk.driveLetter].label}
                        </span>
                      )}
                    </span>
                    <span className="text-[11px] text-muted font-mono">
                      {fmtMB(dk.usedSpaceMB)} / {fmtMB(dk.totalSizeMB)} ({dk.usagePercent.toFixed(1)}%)
                    </span>
                  </div>
                  <ProgressBar p={dk.usagePercent} />
                  <div className="text-[10px] text-muted-3 mt-1">
                    {dk.fileSystem} • {fmtMB(dk.freeSpaceMB)} libre
                  </div>
                </div>
              ))
            ) : (
              <div className="text-muted-2 text-xs">Aucun disque</div>
            )}
          </div>

          <PhysicalDisksPanel inv={inv} sectionCls={sectionCls} sTitleCls={sTitleCls} />

          <div className={sectionCls}>
            <div className={sTitleCls}>
              <Users size={16} strokeWidth={1.75} /> Utilisateurs connectés
            </div>
            {inv?.loggedInUsers?.length > 0 ? (
              <table className={tableCls}>
                <thead>
                  <tr>
                    {['Utilisateur', 'Session', 'Type', 'État', 'Connexion'].map((c) => (
                      <th key={c} className={thCls}>
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {inv.loggedInUsers.map((u, i) => (
                    <tr key={i}>
                      <td className={`${tdCls} font-semibold font-mono`}>{u.username}</td>
                      <td className={tdCls}>{u.sessionName || '—'}</td>
                      <td className={tdCls}>
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                            u.sessionType === 'RDP'
                              ? 'bg-violet-500/20 text-violet-400'
                              : 'bg-blue-500/20 text-blue-400'
                          }`}
                        >
                          {u.sessionType}
                        </span>
                      </td>
                      <td className={tdCls}>
                        <span
                          className={
                            u.state === 'Active' || u.state === 'Actif'
                              ? 'text-emerald-300'
                              : 'text-amber-300'
                          }
                        >
                          {u.state}
                        </span>
                      </td>
                      <td className={`${tdCls} text-[11px] font-mono`}>{fmtDate(u.logonTime)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-muted-2 text-xs">
                {inv?.lastLoggedOnUser ? `Dernier: ${inv.lastLoggedOnUser}` : 'Aucun utilisateur'}
              </div>
            )}
          </div>

          <SystemUpdatesPanel
            agentId={agentId}
            platform={isLinux ? 'linux' : 'windows'}
            inv={inv}
            sectionCls={sectionCls}
            sTitleCls={sTitleCls}
            canInstall={canInstallUpdates}
          />

          {/* Le composant se masque lui-même quand aucun moteur de conteneurs
              n'est présent, donc aucune condition sur la plateforme ici. */}
          <ContainersPanel inv={inv} sectionCls={sectionCls} sTitleCls={sTitleCls} />

          {d.activeAlerts?.length > 0 && (
            <div className={sectionCls}>
              <div className={`${sTitleCls} justify-between`}>
                <span className="inline-flex items-center gap-2">
                  <AlertTriangle size={16} strokeWidth={1.75} /> Alertes actives ({d.activeAlerts.length})
                </span>
                <button
                  onClick={async () => {
                    const ids = d.activeAlerts.map((a) => a.id);
                    await api.post('/platform/alerts/bulk-resolve', ids);
                    const r = await api.get('/devices/' + agentId);
                    setD(r);
                  }}
                  className="bg-ok/12 border border-ok/30 rounded-md px-3 py-1 text-emerald-300 text-[10px] font-semibold cursor-pointer"
                >
                  Tout resoudre
                </button>
              </div>
              {[...d.activeAlerts]
                .sort((a, b) => b.severity - a.severity || new Date(b.occurredAt) - new Date(a.occurredAt))
                .map((al, i) => {
                  const sevColor = ALERT_SEV_COLORS[al.severity] || '#3b82f6';
                  const sevLabel = ALERT_SEV_LABELS[al.severity] || 'Info';
                  // Libellés partagés avec AlertsTable : la liste était dupliquée ici
                  // et avait dérivé (la catégorie CVE, ajoutée en 9, s'affichait
                  // « Autre »).
                  const catLabel = ALERT_CAT_LABELS[al.category] || 'Autre';
                  return (
                    <div
                      key={i}
                      className={`flex items-center gap-2.5 py-2 ${
                        i < d.activeAlerts.length - 1 ? 'border-b border-rule' : ''
                      }`}
                    >
                      <div
                        className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                        style={{ background: sevColor }}
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-fg-2">{al.title}</span>
                          <span
                            className="text-[9px] px-1.5 py-0.5 rounded font-semibold"
                            style={{ background: sevColor + '20', color: sevColor }}
                          >
                            {sevLabel}
                          </span>
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-500/10 text-muted">
                            {catLabel}
                          </span>
                        </div>
                        <div className="text-[10px] text-muted-2 mt-0.5">{fmtDate(al.occurredAt)}</div>
                      </div>
                      {/* `activeAlerts` ne porte pas le contexte machine/client :
                          on le prend sur le device affiché. Déjà rattachée à un
                          bon, la ligne montre le numéro au lieu du bouton. */}
                      {al.comitBonNumero ? (
                        <ComitBonChip
                          compact
                          numero={al.comitBonNumero}
                          linkedAt={al.comitBonLinkedAt}
                          linkedBy={al.comitBonLinkedBy}
                        />
                      ) : (
                        <ComitTaskButton
                          compact
                          texte={buildAlertTaskText(
                            {
                              ...al,
                              computerName: d.agent?.computerName || '',
                              clientName: d.agent?.client?.name || '',
                              siteName: d.agent?.site?.name || '',
                            },
                            { catLabel, sevLabel }
                          )}
                          comitClientId={d.agent?.client?.comitClientId || 0}
                          appareil={d.agent?.computerName || ''}
                          cible={{ alertIds: [al.id] }}
                        />
                      )}
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          await api.post('/platform/alerts/' + al.id + '/resolve');
                          const r = await api.get('/devices/' + agentId);
                          setD(r);
                        }}
                        className="bg-transparent border border-rule-3 rounded-md px-2 py-0.5 text-muted text-[10px] cursor-pointer"
                      >
                        Resoudre
                      </button>
                    </div>
                  );
                })}
            </div>
          )}

          <HostedSitesPanel agentId={agentId} sectionCls={sectionCls} sTitleCls={sTitleCls} />

          {/* Le journal ferme la colonne : il se lit après l'état courant de la
              machine, pas avant. */}
          <DeviceActivityPanel agentId={agentId} sectionCls={sectionCls} sTitleCls={sTitleCls} />
        </div>
      </div>

      {/* Pleine largeur : 24 colonnes d'heures ont besoin d'être lues de gauche
          à droite sans être comprimées, et la légende se pose à côté. */}
      <UptimeHeatmap agentId={agentId} sectionCls={sectionCls} sTitleCls={sTitleCls} />

      {/* Idem : min–max/moyenne sur des mois d'agrégats horaires, pas de place
          dans une colonne de 480px. */}
      <PerformanceHistory agentId={agentId} sectionCls={sectionCls} sTitleCls={sTitleCls} />

      {/* Installed Software */}
      {(() => {
        const swList = inv?.installedSoftware || [];
        const vulns = d.vulnerabilities || [];
        const vulnMap = {};
        vulns.forEach((v) => {
          const key = (v.softwareName + '|' + v.softwareVersion).toLowerCase();
          if (!vulnMap[key]) vulnMap[key] = [];
          vulnMap[key].push(v);
        });
        const vulnByName = {};
        vulns.forEach((v) => {
          const k = v.softwareName.toLowerCase();
          if (!vulnByName[k]) vulnByName[k] = [];
          vulnByName[k].push(v);
        });
        const getSeverity = (sw) => {
          const key = (sw.name + '|' + sw.version).toLowerCase();
          const cves = vulnMap[key] || vulnByName[sw.name.toLowerCase()] || [];
          if (cves.length === 0) return { level: 'secure', cves: [] };
          const maxScore = Math.max(...cves.map((c) => c.cvssScore || 0));
          const sev = cves.find((c) => c.cvssScore === maxScore)?.cvssSeverity || 'UNKNOWN';
          return { level: sev.toLowerCase(), cves, maxScore };
        };

        const enriched = swList.map((sw) => ({ ...sw, security: getSeverity(sw) }));
        const filtered = enriched.filter((sw) => {
          if (swFilter !== 'all') {
            if (swFilter === 'secure' && sw.security.level !== 'secure') return false;
            if (swFilter === 'vulnerable' && sw.security.level === 'secure') return false;
            if (swFilter === 'critical' && sw.security.level !== 'critical') return false;
            if (swFilter === 'high' && sw.security.level !== 'high') return false;
            if (swFilter === 'medium' && sw.security.level !== 'medium') return false;
            if (swFilter === 'low' && sw.security.level !== 'low') return false;
          }
          if (swSearch) {
            const q = swSearch.toLowerCase();
            return (
              sw.name.toLowerCase().includes(q) ||
              sw.version.toLowerCase().includes(q) ||
              (sw.publisher || '').toLowerCase().includes(q)
            );
          }
          return true;
        });

        const sorted = [...filtered].sort((a, b) => {
          if (swSort === 'name') return a.name.localeCompare(b.name);
          if (swSort === 'publisher') return (a.publisher || '').localeCompare(b.publisher || '');
          if (swSort === 'severity') {
            const order = { critical: 0, high: 1, medium: 2, low: 3, unknown: 4, secure: 5 };
            return (order[a.security.level] ?? 5) - (order[b.security.level] ?? 5);
          }
          return 0;
        });
        const totalPages = Math.ceil(sorted.length / SW_PAGE_SIZE);
        const paged = sorted.slice(swPage * SW_PAGE_SIZE, (swPage + 1) * SW_PAGE_SIZE);

        const counts = {
          total: swList.length,
          vulnerable: 0,
          secure: 0,
          critical: 0,
          high: 0,
          medium: 0,
          low: 0,
        };
        enriched.forEach((sw) => {
          if (sw.security.level === 'secure') counts.secure++;
          else {
            counts.vulnerable++;
            counts[sw.security.level] = (counts[sw.security.level] || 0) + 1;
          }
        });

        return (
          <div className={`${sectionCls} mt-0`}>
            <div className={`${sTitleCls} justify-between flex-wrap gap-2.5`}>
              <span className="inline-flex items-center gap-2">
                <Package size={16} strokeWidth={1.75} /> Logiciels installés ({swList.length})
              </span>
              <div className="flex gap-1.5 flex-wrap">
                {counts.critical > 0 && <SevBadge level="critical" text="Critical" count={counts.critical} />}
                {counts.high > 0 && <SevBadge level="high" text="High" count={counts.high} />}
                {counts.medium > 0 && <SevBadge level="medium" text="Medium" count={counts.medium} />}
                {counts.low > 0 && <SevBadge level="low" text="Low" count={counts.low} />}
                <SevBadge level="secure" text="Sécurisé" count={counts.secure} />
              </div>
            </div>

            <div className="flex gap-2.5 mb-3.5 flex-wrap items-center">
              <input
                placeholder="Rechercher un logiciel..."
                value={swSearch}
                onChange={(e) => {
                  setSwSearch(e.target.value);
                  setSwPage(0);
                }}
                className={`${inputCls} flex-1 min-w-[200px]`}
              />
              <select
                value={swFilter}
                onChange={(e) => {
                  setSwFilter(e.target.value);
                  setSwPage(0);
                }}
                className={`${inputCls} cursor-pointer`}
              >
                <option value="all">Tous</option>
                <option value="vulnerable">Vulnérables</option>
                <option value="secure">Sécurisés</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
              <select
                value={swSort}
                onChange={(e) => setSwSort(e.target.value)}
                className={`${inputCls} cursor-pointer`}
              >
                <option value="name">Trier: Nom</option>
                <option value="publisher">Trier: Éditeur</option>
                <option value="severity">Trier: Sévérité</option>
              </select>
            </div>

            <div className="text-[11px] text-muted-2 mb-2.5">
              {filtered.length === swList.length ? '' : `${filtered.length} / ${swList.length} résultats • `}
              Page {swPage + 1} / {Math.max(totalPages, 1)}
            </div>

            {sorted.length > 0 ? (
              <div>
                <table className={`${tableCls} table-fixed`}>
                  <thead>
                    <tr>
                      <th className={`${thCls} w-[30%]`}>Logiciel</th>
                      <th className={`${thCls} w-[12%]`}>Version</th>
                      <th className={`${thCls} w-[20%]`}>Éditeur</th>
                      <th className={`${thCls} w-[12%]`}>Installation</th>
                      <th className={`${thCls} w-[26%]`}>Sécurité</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paged.map((sw, i) => {
                      const sec = sw.security;
                      const hasCves = sec.cves.length > 0;
                      const rowBg =
                        hasCves && sec.level === 'critical'
                          ? 'bg-crit/[.04]'
                          : hasCves && sec.level === 'high'
                            ? 'bg-warn/[.04]'
                            : 'bg-transparent';
                      return (
                        <tr key={i} className={rowBg}>
                          <td className={`${tdCls} font-semibold truncate`}>{sw.name}</td>
                          <td className={`${tdCls} font-mono text-[11px]`}>{sw.version}</td>
                          <td className={`${tdCls} text-muted truncate`}>{sw.publisher || '—'}</td>
                          <td className={`${tdCls} text-[11px] font-mono`}>
                            {sw.installDate ? new Date(sw.installDate).toLocaleDateString('fr-FR') : '—'}
                          </td>
                          <td className={tdCls}>
                            {hasCves ? (
                              <div className="flex flex-col gap-1">
                                <SevBadge
                                  level={sec.level}
                                  text={`${sec.level.toUpperCase()} • ${sec.cves.length} CVE${
                                    sec.cves.length > 1 ? 's' : ''
                                  }${sec.maxScore ? ' • CVSS ' + sec.maxScore.toFixed(1) : ''}`}
                                />
                                <CveLinks cves={sec.cves} />
                                {/* Un seul bon « mettre à jour ce logiciel » pour
                                    toutes ses CVE, plutôt qu'un bon par CVE — et
                                    le serveur étend la liaison au même groupe. */}
                                {sec.cves.find((c) => c.comitBonNumero) ? (
                                  <ComitBonChip
                                    compact
                                    numero={sec.cves.find((c) => c.comitBonNumero).comitBonNumero}
                                    linkedAt={sec.cves.find((c) => c.comitBonNumero).comitBonLinkedAt}
                                    linkedBy={sec.cves.find((c) => c.comitBonNumero).comitBonLinkedBy}
                                  />
                                ) : (
                                  <ComitTaskButton
                                    compact
                                    className="self-start"
                                    texte={buildCveTaskText({
                                      computerName: d.agent?.computerName || '',
                                      clientName: d.agent?.client?.name || '',
                                      softwareName: sw.name,
                                      softwareVersion: sw.version,
                                      cveIds: sec.cves.map((c) => c.cveId),
                                      cvssSeverity: sec.level ? sec.level.toUpperCase() : '',
                                      cvssScore: sec.maxScore ?? null,
                                      detectedAt: sec.cves[0]?.detectedAt,
                                      cveDescription: sec.cves[0]?.cveDescription || '',
                                    })}
                                    comitClientId={d.agent?.client?.comitClientId || 0}
                                    appareil={d.agent?.computerName || ''}
                                    cible={{ vulnerabilityIds: sec.cves.map((c) => c.id) }}
                                  />
                                )}
                              </div>
                            ) : (
                              <SevBadge level="secure" text="Sécurisé" />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {totalPages > 1 && (
                  <div className="flex justify-center gap-1.5 mt-3.5">
                    <button
                      onClick={() => setSwPage(Math.max(0, swPage - 1))}
                      disabled={swPage === 0}
                      className={`bg-rule border-none rounded-md px-3.5 py-1.5 text-xs font-semibold ${
                        swPage === 0 ? 'text-muted-3 cursor-default' : 'text-fg-2 cursor-pointer'
                      }`}
                    >
                      ← Précédent
                    </button>
                    {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                      let page = i;
                      if (totalPages > 7) {
                        if (swPage < 4) page = i;
                        else if (swPage > totalPages - 4) page = totalPages - 7 + i;
                        else page = swPage - 3 + i;
                      }
                      return (
                        <button
                          key={page}
                          onClick={() => setSwPage(page)}
                          className={`border-none rounded-md px-2.5 py-1.5 text-xs font-semibold min-w-[32px] cursor-pointer ${
                            page === swPage ? 'bg-blue-500 text-white' : 'bg-rule text-muted'
                          }`}
                        >
                          {page + 1}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => setSwPage(Math.min(totalPages - 1, swPage + 1))}
                      disabled={swPage >= totalPages - 1}
                      className={`bg-rule border-none rounded-md px-3.5 py-1.5 text-xs font-semibold ${
                        swPage >= totalPages - 1 ? 'text-muted-3 cursor-default' : 'text-fg-2 cursor-pointer'
                      }`}
                    >
                      Suivant →
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-muted-2 text-xs py-3">
                {swSearch || swFilter !== 'all'
                  ? 'Aucun résultat pour ces filtres'
                  : 'Aucun logiciel inventorié'}
              </div>
            )}
          </div>
        );
      })()}

      {/* Network Adapters */}
      {inv?.networkAdapters?.length > 0 &&
        (() => {
          const filteredNics = nicOnlyUp
            ? inv.networkAdapters.filter((n) => n.status === 'Up')
            : inv.networkAdapters;
          const upCount = inv.networkAdapters.filter((n) => n.status === 'Up').length;
          return (
            <div className={sectionCls}>
              <div className={`${sTitleCls} justify-between`}>
                <span className="inline-flex items-center gap-2">
                  <Network size={16} strokeWidth={1.75} /> Cartes réseau (
                  {nicOnlyUp
                    ? upCount + '/' + inv.networkAdapters.length + ' actives'
                    : inv.networkAdapters.length}
                  )
                </span>
                <button
                  onClick={() => {
                    const next = !nicOnlyUp;
                    setNicOnlyUp(next);
                    localStorage.setItem('mh_nic_filter', next ? 'up' : 'all');
                  }}
                  className={`rounded-md px-3 py-1 text-[11px] font-semibold border cursor-pointer ${
                    nicOnlyUp
                      ? 'bg-ok/15 border-ok/30 text-emerald-300'
                      : 'bg-slate-500/15 border-rule text-muted'
                  }`}
                >
                  {nicOnlyUp ? 'Actives uniquement' : 'Toutes les cartes'}
                </button>
              </div>
              {filteredNics.map((nic, i) => {
                const isUp = nic.status === 'Up';
                const fmtMac = (m) => (m && m.length === 12 ? m.match(/.{2}/g).join(':') : m || '—');
                return (
                  <div
                    key={i}
                    className={`rounded-xl px-4 py-3.5 border ${
                      isUp ? 'bg-ok/[.04] border-ok/25' : 'bg-sidebar border-rule'
                    } ${i < inv.networkAdapters.length - 1 ? 'mb-2.5' : ''}`}
                  >
                    <div className="flex justify-between items-center mb-2.5">
                      <div className="flex items-center gap-2.5">
                        <div className={`w-2 h-2 rounded-full ${isUp ? 'bg-ok' : 'bg-crit'}`} />
                        <span className="text-sm font-bold text-fg">{nic.name}</span>
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                            NIC_TYPE_CLS[nic.type] || 'bg-slate-500/15 text-muted-2'
                          }`}
                        >
                          {nic.type}
                        </span>
                      </div>
                      <span
                        className={`text-[11px] font-semibold ${isUp ? 'text-emerald-300' : 'text-red-300'}`}
                      >
                        {isUp ? 'Actif' : 'Inactif'}
                      </span>
                    </div>
                    {nic.description && (
                      <div className="text-[11px] text-muted-2 mb-2.5">{nic.description}</div>
                    )}
                    <div className="mb-2.5">
                      <span
                        className={`px-2.5 py-0.5 rounded text-[10px] font-bold tracking-wide uppercase border ${
                          nic.dhcpEnabled
                            ? 'bg-blue-500/15 border-blue-500/30 text-blue-400'
                            : 'bg-warn/15 border-warn/30 text-amber-300'
                        }`}
                      >
                        {nic.dhcpEnabled ? 'DHCP' : 'Statique'}
                      </span>
                    </div>
                    <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-2.5">
                      <KV label="Adresse MAC" value={fmtMac(nic.macAddress)} />
                      <KV
                        label="Vitesse"
                        value={
                          isUp && nic.speedMbps > 0
                            ? nic.speedMbps >= 1000
                              ? nic.speedMbps / 1000 + ' Gbps'
                              : nic.speedMbps + ' Mbps'
                            : '—'
                        }
                      />
                      <KV
                        label="IPv4"
                        value={
                          nic.ipv4Addresses?.length > 0
                            ? nic.ipv4Addresses
                                .map(
                                  (ip, j) =>
                                    ip + (nic.ipv4SubnetMasks?.[j] ? ' / ' + nic.ipv4SubnetMasks[j] : '')
                                )
                                .join(', ')
                            : '—'
                        }
                      />
                      <KV
                        label="IPv6"
                        value={nic.ipv6Addresses?.length > 0 ? nic.ipv6Addresses.join(', ') : '—'}
                      />
                      <KV
                        label="Passerelle"
                        value={nic.gatewayAddresses?.length > 0 ? nic.gatewayAddresses.join(', ') : '—'}
                      />
                      <KV
                        label="Serveurs DNS"
                        value={nic.dnsServers?.length > 0 ? nic.dnsServers.join(', ') : '—'}
                      />
                      {nic.dhcpEnabled && <KV label="Serveur DHCP" value={nic.dhcpServer || '—'} />}
                      <KV label="Suffixe DNS" value={nic.dnsSuffix || '—'} />
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}
    </div>
  );
}
